import { supabaseAdmin } from './supabase-admin';
import { MEDIA_TYPE_TO_BUCKET } from './supabase-storage';

/**
 * Ensures that all required storage buckets exist in Supabase
 * Creates any missing buckets with public read access
 */
export async function ensureStorageBuckets(): Promise<{
  success: boolean;
  created: string[];
  existing: string[];
  failed: string[];
  error?: string;
}> {
  const result = {
    success: true,
    created: [] as string[],
    existing: [] as string[],
    failed: [] as string[],
    error: undefined as string | undefined
  };

  try {
    // Get unique bucket names from our mapping
    const bucketNames = [...new Set(Object.values(MEDIA_TYPE_TO_BUCKET))];
    
    // Get list of existing buckets
    const { data: existingBuckets, error: listError } = await supabaseAdmin.storage.listBuckets();
    
    if (listError) {
      console.error('Error listing buckets:', listError);
      return {
        ...result,
        success: false,
        error: `Failed to list buckets: ${listError.message}`
      };
    }

    const existingBucketNames = existingBuckets?.map(bucket => bucket.name) || [];
    
    // Create each bucket that doesn't exist
    for (const bucketName of bucketNames) {
      if (existingBucketNames.includes(bucketName)) {
        result.existing.push(bucketName);
        continue;
      }
      
      // Create the bucket with public access
      const { error: createError } = await supabaseAdmin.storage.createBucket(bucketName, {
        public: true, // Make bucket publicly accessible
        fileSizeLimit: 100 * 1024 * 1024 // 100MB limit
      });
      
      if (createError) {
        console.error(`Error creating bucket ${bucketName}:`, createError);
        result.failed.push(bucketName);
        result.success = false;
      } else {
        console.log(`Created bucket: ${bucketName}`);
        result.created.push(bucketName);
        
        // Set bucket to public
        const { error: updateError } = await supabaseAdmin.storage.updateBucket(bucketName, {
          public: true
        });
        
        if (updateError) {
          console.warn(`Created bucket ${bucketName} but failed to set public access:`, updateError);
        }
        
        // Create RLS policy to allow authenticated users to upload files
        try {
          // Allow authenticated users to upload files
          const { error: policyError } = await supabaseAdmin.rpc('create_storage_policy', {
            bucket_name: bucketName,
            policy_name: `${bucketName}_auth_insert`,
            policy_definition: 'auth.role() = \'authenticated\'',
            policy_operation: 'INSERT'
          });
          
          if (policyError) {
            console.warn(`Failed to create INSERT policy for ${bucketName}:`, policyError);
          }
          
          // Allow authenticated users to update their own files
          const { error: updatePolicyError } = await supabaseAdmin.rpc('create_storage_policy', {
            bucket_name: bucketName,
            policy_name: `${bucketName}_auth_update`,
            policy_definition: 'auth.role() = \'authenticated\'',
            policy_operation: 'UPDATE'
          });
          
          if (updatePolicyError) {
            console.warn(`Failed to create UPDATE policy for ${bucketName}:`, updatePolicyError);
          }
          
          // Allow authenticated users to delete their own files
          const { error: deletePolicyError } = await supabaseAdmin.rpc('create_storage_policy', {
            bucket_name: bucketName,
            policy_name: `${bucketName}_auth_delete`,
            policy_definition: 'auth.role() = \'authenticated\'',
            policy_operation: 'DELETE'
          });
          
          if (deletePolicyError) {
            console.warn(`Failed to create DELETE policy for ${bucketName}:`, deletePolicyError);
          }
        } catch (policyError) {
          console.warn(`Error setting up policies for ${bucketName}:`, policyError);
        }
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error ensuring storage buckets:', error);
    return {
      ...result,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error creating buckets'
    };
  }
}

/**
 * Checks if a specific bucket exists
 */
export async function checkBucketExists(bucketName: string): Promise<boolean> {
  try {
    const { data: buckets, error } = await supabaseAdmin.storage.listBuckets();
    
    if (error) {
      console.error('Error checking bucket existence:', error);
      return false;
    }
    
    return buckets?.some(bucket => bucket.name === bucketName) || false;
  } catch (error) {
    console.error('Error checking bucket existence:', error);
    return false;
  }
}

export default ensureStorageBuckets;
